import React, { CSSProperties, FocusEventHandler, JSX } from 'react';
import classNames from 'classnames';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import MuiIconButton from '@mui/material/IconButton';
import { ButtonBaseActions } from '@mui/material/ButtonBase';
import { CommonPropsAndTypes } from '../../_Types/StandardComponentProps';
import EDTooltip, { TooltipPlacement } from '../../Tooltip/EDTooltip';
import { IconType } from '../../Icon/Utils/IconDefinitions';
import EDIcon from '../../Icon/EDIcon';
import { styled, SxProps } from '@mui/material';
import { spacing, SpacingProps } from '@mui/system';
import { isNotSpacingProp } from '../../../Utils/spacingUtils';

export interface IActions extends ButtonBaseActions {}
type IColor = CommonPropsAndTypes.ExtendedColorType;

interface IInnerProps extends IProps {
  innerRef: React.Ref<HTMLButtonElement>;
}

interface IProps extends SpacingProps {
  icon: IconType | JSX.Element | undefined | null;
  strictHeight?: boolean;
  noLeftMargin?: boolean;
  noRightMargin?: boolean;
  noTopMargin?: boolean;
  noBottomMargin?: boolean;
  moveRightPadding?: boolean;
  invertColorOnHover?: boolean;
  disabled?: boolean;
  disabledReason?: React.ReactNode;
  disableBackground?: boolean;
  disableRipple?: boolean;
  maskButton?: boolean;
  color?: CommonPropsAndTypes.ColorType;
  iconColor?: IColor;
  iconSize?: CommonPropsAndTypes.StandardSizesType | 'default' | 'extraLarge';
  tooltip: string;
  /** Tooltipen (som är obligatorisk) används bara i ariaLabel men visas inte för "vanliga" användare */
  tooltipInAriaLabelOnly?: boolean;
  tooltipPlacement?: TooltipPlacement;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseDown?: React.MouseEventHandler<HTMLElement>;
  action?: (actions: IActions) => void;
  testId?: string;
  disableTabbing?: boolean;
  incrementer?: boolean;
  sx?: SxProps<Theme>;
  primaryBackground?: boolean;
  colorWhite?: boolean;
  /**Används om man vill att elementet ska reagera på pekarhändelser */
  buttonTooltipSpan?: boolean;
  onFocus?: FocusEventHandler<HTMLButtonElement>;
  classes?: Partial<Record<keyof ReturnType<typeof styles>, string>>;
  className?: string;
  style?: CSSProperties;
}

const styles = (theme: Theme, _params: unknown, classes: any) => ({
  invertColorOnHoverStyle: {
    '&:hover': {
      backgroundColor: theme.palette.grey['500'],
      color: theme.customProperties.Fade.LightOnDark.highEmphasis,
    },
  },
  backgroundDisabled: {
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  primaryBackgroundClass: {
    background: theme.palette.primary.main,
    '&:hover': {
      background: theme.palette.primary.dark,
    },
  },
  /** Margins */
  noneMargin: {
    margin: theme.spacing(0),
  },
  denseMargin: {
    margin: theme.spacing(0.5),
  },
  normalMargin: {
    margin: theme.spacing(1),
  },
  noLeftMarginStyle: {
    marginLeft: theme.spacing(0),
  },
  noRightMarginStyle: {
    marginRight: theme.spacing(0),
  },
  noTopMarginStyle: {
    marginTop: theme.spacing(0),
  },
  noBottomMarginStyle: {
    marginBottom: theme.spacing(0),
  },
  moveRightPaddingStyle: {
    marginRight: theme.spacing(-0.5),
  },

  /** Paddings */
  nonePadding: {
    padding: theme.spacing(0),
  },
  densePadding: {
    padding: theme.spacing(0.5),
  },
  mediumPadding: {
    padding: theme.spacing(0.75),
  },

  /** Height **/
  strictHeightNoPadding: {
    height: theme.spacing(3),
  },

  strictHeightDensePadding: {
    height: theme.spacing(4),
  },

  strictHeightNormal: {
    height: theme.spacing(6),
  },
  colorInherit: {
    color: 'inherit',
  },

  /** Variant **/

  maskButtonBehavior: {
    cursor: 'default',
  },

  /** MUI Classes */
  /** Applies to the root element */
  root: {
    [`&.${classes.noLeftMarginStyle}`]: {
      marginLeft: theme.spacing(0),
    },
    [`&.${classes.noRightMarginStyle}`]: {
      marginRight: theme.spacing(0),
    },
    [`&.${classes.noTopMarginStyle}`]: {
      marginTop: theme.spacing(0),
    },
    [`&.${classes.noBottomMarginStyle}`]: {
      marginBottom: theme.spacing(0),
    },
  },
  /** Styles applied to the children container element */
  label: {},

  tooltipPopper: {},
  tooltipSpan: {
    display: 'inline-block',
  },
  /** Incrementer style */
  incrementerStyle: {
    backgroundColor: theme.palette.grey['700'],
    color: theme.customProperties.Fade.LightOnDark.highEmphasis,
    borderRadius: theme.spacing(0.25),
    padding: theme.spacing(1),
    '&:hover': {
      backgroundColor: theme.palette.grey['600'],
    },
  },
  incrementerStyleDisabled: {
    [`&.${classes.disabled}`]: {
      backgroundColor: theme.palette.grey['300'],
      borderRadius: theme.spacing(0.25),
      padding: theme.spacing(1),
    },
  },
  disabled: {},
  tooltipSpanPointerEventsAuto: {
    pointerEvents: 'auto' as React.CSSProperties['pointerEvents'],
  },
  colorWhiteClass: {
    color: theme.palette.common.white,
  },
});

/**
 * En ikon som fungerar som en knapp.
 */
export const EDIconButtonBase: React.FunctionComponent<
  React.PropsWithChildren<IInnerProps>
> = props => {
  const {
    noLeftMargin,
    noRightMargin,
    noTopMargin,
    noBottomMargin,
    moveRightPadding,
    invertColorOnHover,
    disableBackground,
    icon,
    incrementer,
    margin = 'normal',
    padding,
    strictHeight,
    iconColor,
    iconSize,
    maskButton,
    testId,
    disabledReason,
    disableTabbing,
    tooltipPlacement,
    tooltip,
    innerRef,
    primaryBackground,
    buttonTooltipSpan,
    colorWhite,
    tooltipInAriaLabelOnly,
    ...rest
  } = props;
  const classes = withStyles.getClasses(props);
  const {
    root,
    noLeftMarginStyle,
    noRightMarginStyle,
    noTopMarginStyle,
    noBottomMarginStyle,
    moveRightPaddingStyle,
    invertColorOnHoverStyle,
    backgroundDisabled,
    noneMargin,
    denseMargin,
    normalMargin,
    nonePadding,
    densePadding,
    mediumPadding,
    strictHeightNoPadding,
    strictHeightDensePadding,
    strictHeightNormal,
    maskButtonBehavior,
    tooltipPopper,
    tooltipSpan,
    incrementerStyle,
    incrementerStyleDisabled,
    disabled,
    tooltipSpanPointerEventsAuto,
    colorWhiteClass,
    primaryBackgroundClass,
    ...muiClasses
  } = classes;

  const className = classNames({
    [noLeftMarginStyle]: noLeftMargin,
    [noRightMarginStyle]: noRightMargin,
    [noTopMarginStyle]: noTopMargin,
    [noBottomMarginStyle]: noBottomMargin,
    [moveRightPaddingStyle]: moveRightPadding,
    [invertColorOnHoverStyle]: invertColorOnHover,
    [incrementerStyle]: incrementer && !props.disabled,
    [incrementerStyleDisabled]: incrementer && props.disabled,
  });

  const mergedClasses = {
    ...muiClasses,
    root: classNames(root, {
      [backgroundDisabled]: disableBackground,
      [noneMargin]: margin === 'none',
      [denseMargin]: margin === 'dense',
      [normalMargin]: margin === 'normal',
      [nonePadding]: padding === 'none',
      [densePadding]: padding === 'dense',
      [mediumPadding]: padding === 'medium',
      [strictHeightNoPadding]: strictHeight && padding === 'none',
      [strictHeightDensePadding]: strictHeight && padding === 'dense',
      [strictHeightNormal]: strictHeight && !padding,
      [maskButtonBehavior]: maskButton,
    }),
    disabled: disabled,
  };

  const iconButton = (
    <MuiIconButton
      aria-label={tooltip}
      tabIndex={disableTabbing ? -1 : undefined}
      {...rest}
      ref={innerRef}
      data-testid={testId}
      classes={mergedClasses}
      className={classNames(className, props.className, {
        [classes.colorInherit]: iconColor === 'inherit',
        [classes.colorWhiteClass]: props.colorWhite,
        [classes.primaryBackgroundClass]: props.primaryBackground,
        [classes.tooltipSpanPointerEventsAuto]: props.buttonTooltipSpan,
      })}
      size={iconSize === 'medium' || iconSize === 'small' ? 'medium' : 'large'}
      onFocus={props.onFocus}
    >
      {typeof icon === 'string' ? (
        <EDIcon
          disableTabbing={disableTabbing}
          color={
            props.disabled
              ? 'inherit'
              : incrementer
              ? 'inherit'
              : iconColor
              ? iconColor
              : props.color
          }
          iconType={icon as IconType}
          size={iconSize ? iconSize : 'default'}
        />
      ) : (
        icon
      )}
    </MuiIconButton>
  );

  if (props.disabled && disabledReason) {
    return (
      <EDTooltip
        title={disabledReason}
        placement={tooltipPlacement}
        enableClickOnTouch
      >
        {<span className={tooltipSpan}>{iconButton}</span>}
      </EDTooltip>
    );
  }

  if (tooltip) {
    return (
      <EDTooltip
        title={tooltipInAriaLabelOnly ? '' : tooltip}
        placement={tooltipPlacement}
      >
        {<span className={tooltipSpan}>{iconButton}</span>}
      </EDTooltip>
    );
  }

  return iconButton;
};

const EDEnhancedIconButton = withStyles(
  React.forwardRef((props: IProps, ref: React.Ref<HTMLButtonElement>) => (
    <EDIconButtonBase {...props} innerRef={ref} />
  )),
  styles
);
const EDIconButton: React.FC<
  React.PropsWithChildren<
    Omit<IProps & React.RefAttributes<HTMLButtonElement>, 'classes'>
  >
> = styled(EDEnhancedIconButton, {
  name: 'EDIconButton',
  shouldForwardProp: prop =>
    isNotSpacingProp(prop) || prop === 'padding' || prop == 'margin',
})(spacing);

export type { IProps as IIconButtonProps };
export default EDIconButton;
